// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Service for installment-related API calls

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function getAuthToken() {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
}

export async function fetchPaidInstallments(page = 1, limit = 16, filters = {}) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query parameters
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('limit', limit);

  if (filters.date) {
    queryParams.append('date', filters.date);
  }
  if (filters.projectId) {
    queryParams.append('project', filters.projectId);
  }
  if (filters.customerId) {
    queryParams.append('customer', filters.customerId);
  }

  const url = `${API_BASE_URL}api/v1/dashboard/installment/previous?${queryParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch paid installments';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function fetchUnpaidInstallments(page = 1, limit = 16, filters = {}) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  // Build query parameters
  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('limit', limit);

  if (filters.date) {
    queryParams.append('date', filters.date);
  }
  if (filters.projectId) {
    queryParams.append('project', filters.projectId);
  }
  if (filters.customerId) {
    queryParams.append('customer', filters.customerId);
  }

  const url = `${API_BASE_URL}api/v1/dashboard/installment/remaining?${queryParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch unpaid installments';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function closeInstallment(installmentId) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  const url = `${API_BASE_URL}api/v1/dashboard/installment/close?installment_id=${installmentId}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to close installment';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function deleteInstallment(installmentId) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  const url = `${API_BASE_URL}api/v1/dashboard/installment/delete/${installmentId}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to delete installment';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function fetchCustomerRemainingInstallments(userId, page = 1, limit = 16) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  const url = `${API_BASE_URL}api/v1/dashboard/installment/remaining-customer?user_id=${userId}&page=${page}&limit=${limit}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch remaining installments';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function fetchCustomerPreviousInstallments(userId, page = 1, limit = 16) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  const url = `${API_BASE_URL}api/v1/dashboard/installment/previous-customer?user_id=${userId}&page=${page}&limit=${limit}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch previous installments';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function exportInstallments(status = 'paid') {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  status = status.trim();
  const url = `${API_BASE_URL}api/v1/dashboard/installment/export?status=${status}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to export installments';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
  // Get the blob from the response
  const blob = await response.blob();
  // Create a download link
  const urlObj = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = urlObj;
  a.download = `installments_export_${status}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(urlObj);
  document.body.removeChild(a);
  return { success: true };
}

export async function openInstallment(installmentId) {
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/installment/open?installment_id=${installmentId}`;
  const response = await fetch(url, {
    method: 'PATCH',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to open installment';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}